(() => {
  const ROOT_SELECTOR = '[data-ln-cart-bundle-upgrade]';
  const BUTTON_SELECTOR = '[data-ln-cart-upgrade-submit]';
  const SOURCE = 'ln-cart-bundle-upgrade';
  let requestPending = false;

  const analyticsPayload = (root, extra = {}) => ({
    surface: 'cart_page',
    mode: 'upgrade',
    bundle_id: positiveNumber(root?.dataset.lnBundleId) || undefined,
    bundle_handle: root?.dataset.lnBundleHandle,
    bundle_title: root?.dataset.bundleTitle,
    variant_id: positiveNumber(root?.dataset.bundleVariantId) || undefined,
    value: Number(root?.dataset.lnValue) || undefined,
    currency: root?.dataset.lnCurrency,
    replace_count: Number(root?.dataset.lnReplaceCount) || undefined,
    upgrade_delta: Number(root?.dataset.lnUpgradeDelta) || 0,
    cart_value_before: Number(root?.dataset.lnCartValueBefore) || 0,
    projected_total: Number(root?.dataset.lnProjectedTotal) || 0,
    unlocks_free_shipping: root?.dataset.lnUnlocksFreeShipping === 'true',
    component_one: root?.dataset.lnComponentOne,
    component_two: root?.dataset.lnComponentTwo,
    ...extra,
  });

  const trackAnalytics = (name, root, extra = {}) => {
    try {
      window.LashNovaAnalytics?.track?.(name, analyticsPayload(root, extra));
    } catch (_error) {
      // Analytics must never interrupt the cart transaction.
    }
  };

  const analyticsErrorCode = (error) => {
    try {
      return window.LashNovaAnalytics?.classifyError?.(error) || 'cart_update_failed';
    } catch (_analyticsError) {
      return 'cart_update_failed';
    }
  };

  const positiveNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  };

  const messageFromResponse = (data, fallback) => {
    const message = data?.description || data?.message || data?.errors;
    return typeof message === 'string' && message ? message : fallback;
  };

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.headers || {}),
      },
    });

    let data;
    try {
      data = await response.json();
    } catch (_error) {
      throw new Error('The cart could not be updated. Please try again.');
    }

    if (!response.ok || data?.status || data?.errors) {
      throw new Error(messageFromResponse(data, 'The cart could not be updated. Please try again.'));
    }

    return data;
  };

  const getSectionTargets = () => {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');

    return [
      { id: 'main-cart-items', section: items?.dataset.id, selector: '.js-contents' },
      { id: 'main-cart-footer', section: footer?.dataset.id, selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
    ].filter((target) => target.section);
  };

  const withSections = (payload, targets) => ({
    ...payload,
    sections: targets.map((target) => target.section).slice(0, 5),
    sections_url: window.location.pathname,
  });

  const addBundle = (variantId, targets) =>
    requestJson(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withSections({ items: [{ id: variantId, quantity: 1 }] }, targets)),
    });

  const updateLines = (updates, targets) =>
    requestJson(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withSections({ updates }, targets)),
    });

  const fetchCart = () => requestJson(`${window.routes.cart_url}.js`);

  const isNestedLine = (item) => Boolean(item?.parent_relationship?.parent || item?.parent_relationship?.parent_key);

  const addedLineFromResponse = (response) => response?.items?.[0] || response;

  const renderSections = (cartState, targets) => {
    let rendered = 0;

    targets.forEach((target) => {
      const targetRoot = document.getElementById(target.id);
      const sectionHTML = cartState?.sections?.[target.section];
      if (!targetRoot || typeof sectionHTML !== 'string') return;

      const sourceDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const source = sourceDocument.querySelector(target.selector);
      const destination = targetRoot.querySelector(target.selector) || targetRoot;
      if (!source || !destination) return;

      destination.innerHTML = source.innerHTML;
      rendered += 1;
    });

    return rendered > 0;
  };

  const publishCartUpdate = (cartData) => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    try {
      Promise.resolve(
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: SOURCE,
          cartData,
        })
      ).catch((error) => console.error('Cart update subscriber failed:', error));
    } catch (error) {
      console.error('Cart update subscriber failed:', error);
    }
  };

  const announce = (message) => {
    const liveRegion = document.getElementById('cart-live-region-text');
    if (!liveRegion) return;
    liveRegion.textContent = message;
    liveRegion.setAttribute('aria-hidden', 'false');
  };

  const setBusy = (root, button, busy) => {
    root?.classList.toggle('is-loading', busy);
    if (button) {
      button.disabled = busy;
      button.setAttribute('aria-disabled', String(busy));
      button.classList.toggle('loading', busy);
      button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
    }
  };

  const showError = (message) => {
    const currentRoot = document.querySelector(ROOT_SELECTOR);
    const inlineError = currentRoot?.querySelector('[data-ln-cart-upgrade-error]');
    if (inlineError) {
      inlineError.textContent = message;
      inlineError.hidden = false;
      return;
    }

    const cartError = document.getElementById('cart-errors');
    if (cartError) cartError.textContent = message;
  };

  const rollbackBundle = async (addResponse, bundleVariantId, targets) => {
    const cart = await fetchCart();
    const addedKey = addedLineFromResponse(addResponse)?.key;
    const addedLine =
      cart.items.find((item) => addedKey && item.key === addedKey) ||
      cart.items.find(
        (item) => positiveNumber(item.variant_id) === bundleVariantId && !isNestedLine(item)
      );

    if (!addedLine) throw new Error('The added Bundle could not be restored.');

    return updateLines({ [addedLine.key]: Math.max(0, addedLine.quantity - 1) }, targets);
  };

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
    if (!button) return;

    event.preventDefault();
    const root = button.closest(ROOT_SELECTOR);
    if (!root || requestPending || root.classList.contains('is-loading')) return;

    trackAnalytics('ln_bundle_offer_click', root);

    const bundleVariantId = positiveNumber(root.dataset.bundleVariantId);
    const bundleTitle = root.dataset.bundleTitle || 'Duo';
    const sourceLines = Array.from(root.querySelectorAll('[data-ln-cart-upgrade-line]'))
      .map((line) => ({
        key: line.dataset.key || '',
        quantity: positiveNumber(line.dataset.quantity),
      }))
      .filter((line) => line.key && line.quantity);

    if (!bundleVariantId || sourceLines.length === 0) {
      trackAnalytics('ln_bundle_upgrade_error', root, {
        error_code: 'invalid_product_state',
        final_state: 'unchanged',
      });
      showError('This upgrade is not ready. Please refresh your cart and try again.');
      return;
    }

    requestPending = true;
    setBusy(root, button, true);
    const targets = getSectionTargets();
    let addResponse;
    let analyticsOutcomeTracked = false;

    const trackOutcome = (name, details = {}) => {
      if (analyticsOutcomeTracked) return;
      analyticsOutcomeTracked = true;
      trackAnalytics(name, root, details);
    };

    try {
      addResponse = await addBundle(bundleVariantId, targets);

      const updates = sourceLines.reduce((result, line) => {
        result[line.key] = Math.max(0, line.quantity - 1);
        return result;
      }, {});

      const finalCart = await updateLines(updates, targets);
      renderSections(finalCart, targets);
      publishCartUpdate(finalCart);
      trackOutcome('ln_bundle_upgrade_success', {
        replaced_quantity: sourceLines.length,
        final_state: 'bundle_replaced_singles',
      });
      announce(`Your matching single style was upgraded to ${bundleTitle}.`);
    } catch (error) {
      if (!addResponse) {
        trackOutcome('ln_bundle_upgrade_error', {
          error_code: analyticsErrorCode(error),
          rollback_status: 'not_needed',
          final_state: 'unchanged',
        });
        showError(error?.message || 'The cart could not be updated. Please try again.');
      } else {
        try {
          const restoredCart = await rollbackBundle(addResponse, bundleVariantId, targets);
          renderSections(restoredCart, targets);
          publishCartUpdate(restoredCart);
          trackOutcome('ln_bundle_upgrade_error', {
            error_code: analyticsErrorCode(error),
            rollback_status: 'restored',
            final_state: 'original_cart_kept',
          });
          showError(`We couldn't complete the upgrade. Your original cart was kept.`);
        } catch (_rollbackError) {
          renderSections(addResponse, targets);
          publishCartUpdate(addResponse);
          trackOutcome('ln_bundle_upgrade_error', {
            error_code: analyticsErrorCode(error),
            rollback_status: 'failed',
            final_state: 'bundle_and_singles_present',
          });
          showError(`${bundleTitle} was added, but the original item could not be removed. Please review your cart.`);
        }
      }
    } finally {
      requestPending = false;
      if (root.isConnected) setBusy(root, button, false);
    }
  });
})();
